import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, LoadingState, CardLoadingSkeleton, TableLoadingSkeleton } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, EnvelopeSimple, MagnifyingGlass, Crown, Users as UsersIcon, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRESET_DESIGNATIONS = ["President", "Vice President", "Secretary", "Treasurer", "Committee Member", "Custom"];

export default function Directory() {
    const { user } = useAuth();
    const [committee, setCommittee] = useState([]);
    const [members, setMembers] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const canEdit = user?.role === "admin" || user?.role === "committee";
    const [editingMember, setEditingMember] = useState(null);
    const [designationChoice, setDesignationChoice] = useState("");
    const [customDesignation, setCustomDesignation] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [c, d] = await Promise.all([api.get("/committee"), api.get("/directory")]);
                setCommittee(c.data);
                setMembers(d.data);
            } catch (e) { toast.error(formatError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return members;
        return members.filter((m) =>
            [m.name, m.flat_label || "", m.relationship_label || ""].join(" ").toLowerCase().includes(t)
        );
    }, [members, q]);

    const [revealed, setRevealed] = useState({});
    const [revealing, setRevealing] = useState(new Set());

    async function revealMobile(m) {
        const key = `${m.resident_id}_${m.flat_id}`;
        if (revealed[key] || revealing.has(key)) return;
        setRevealing((prev) => new Set(prev).add(key));
        try {
            const { data } = await api.post("/directory/reveal-mobile", { resident_id: m.resident_id, flat_id: m.flat_id });
            setRevealed((prev) => ({ ...prev, [key]: data.mobile }));
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setRevealing((prev) => { const n = new Set(prev); n.delete(key); return n; });
        }
    }

    function openEditDesignation(member) {
        setEditingMember(member);
        const current = member.designation || "";
        if (current && PRESET_DESIGNATIONS.includes(current)) {
            setDesignationChoice(current);
            setCustomDesignation("");
        } else if (current) {
            setDesignationChoice("Custom");
            setCustomDesignation(current);
        } else {
            setDesignationChoice("");
            setCustomDesignation("");
        }
    }

    async function saveDesignation() {
        if (!editingMember) return;
        const value = designationChoice === "Custom" ? customDesignation.trim() : designationChoice;
        setSaving(true);
        try {
            const { data } = await api.patch(`/users/${editingMember.id}/designation`, { designation: value });
            setCommittee((prev) => prev.map((m) => (m.id === data.id ? { ...m, designation: data.designation } : m)));
            setMembers((prev) => prev.map((m) => (m.id === data.id ? { ...m, designation: data.designation } : m)));
            toast.success("Designation updated");
            setEditingMember(null);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Contacts"
                title="Member directory"
                description="Meet your committee and find any resident by name, flat or role."
            />

            {/* Committee cards */}
            <div className="mb-10">
                <div className="flex items-center gap-2 mb-4">
                    <Crown size={18} weight="duotone" className="text-brand-action" />
                    <h2 className="font-heading text-xl text-brand-ink">Your committee</h2>
                    <span className="text-xs text-brand-inkSoft">— reach out for any concern</span>
                </div>
                {loading ? (
                    <CardLoadingSkeleton count={3} />
                ) : committee.length === 0 ? (
                    <EmptyState title="No committee members yet" description="Ask your admin to promote a resident to the committee." />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {committee.map((m) => (
                            <div key={m.id} data-testid={`committee-card-${m.id}`}
                                className="bg-white border border-brand-line rounded-sm p-5 hover:-translate-y-1 hover:shadow-lg transition-transform duration-200">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="font-heading text-lg text-brand-ink truncate">{m.name}</p>
                                        <p className="text-xs text-brand-inkSoft mt-0.5">{m.flat_label ? `Flat ${m.flat_label}` : "—"}</p>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <Chip variant={m.role === "admin" ? "danger" : "warn"}>{m.role}</Chip>
                                        {canEdit && (
                                            <button
                                                data-testid={`edit-designation-${m.id}`}
                                                onClick={() => openEditDesignation(m)}
                                                className="w-6 h-6 rounded-full flex items-center justify-center text-brand-inkSoft hover:text-brand-action hover:bg-brand-sage/40 transition-colors"
                                                title="Edit designation"
                                            >
                                                <PencilSimple size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-2">
                                    {m.designation ? (
                                        <span data-testid={`designation-${m.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-brand-action bg-brand-action/10 px-2 py-1 rounded-full">
                                            <Crown size={12} weight="fill" /> {m.designation}
                                        </span>
                                    ) : canEdit ? (
                                        <button
                                            data-testid={`add-designation-${m.id}`}
                                            onClick={() => openEditDesignation(m)}
                                            className="text-xs text-brand-inkSoft hover:text-brand-action underline underline-offset-2"
                                        >
                                            + Add designation
                                        </button>
                                    ) : null}
                                </div>
                                <div className="mt-4 space-y-2 text-sm">
                                    {!m.email.endsWith("@no-email.maintyn.local") && (
                                        <a href={`mailto:${m.email}`} className="flex items-center gap-2 text-brand-ink hover:text-brand-action group">
                                            <EnvelopeSimple size={16} className="text-brand-inkSoft group-hover:text-brand-action" /> {m.email}
                                        </a>
                                    )}
                                    {m.phone && (
                                        <a href={`tel:${m.phone}`} className="flex items-center gap-2 text-brand-ink hover:text-brand-action group">
                                            <Phone size={16} className="text-brand-inkSoft group-hover:text-brand-action" /> {m.phone}
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* All members */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <UsersIcon size={18} weight="duotone" className="text-brand-ink" />
                    <h2 className="font-heading text-xl text-brand-ink">All members</h2>
                    <span className="text-xs text-brand-inkSoft">— {members.length} total</span>
                </div>
                {!loading && (
                    <div className="relative mb-4 max-w-sm">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name, flat, relationship..." className="pl-9 rounded-sm border-brand-line bg-white h-10" />
                    </div>
                )}
                {loading ? (
                    <TableLoadingSkeleton rows={8} cols={4} />
                ) : filtered.length === 0 ? (
                    <EmptyState title="No matches" description="Try a different search." />
                ) : (
                    <div className="bg-white border border-brand-line rounded-sm overflow-x-auto">
                        <table className="w-full text-sm min-w-[560px]">
                            <thead>
                                <tr className="bg-brand-bg border-b border-brand-line text-left">
                                    {["Name", "Flat", "Relationship", "Mobile"].map(h => (
                                        <th key={h} className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((m) => {
                                    const key = `${m.resident_id}_${m.flat_id}`;
                                    const fullMobile = revealed[key];
                                    return (
                                        <tr key={key} data-testid={`directory-row-${key}`} className="border-b border-brand-line/60 hover:bg-brand-bg/50">
                                            <td className="py-3 px-4 font-medium text-brand-ink whitespace-nowrap">{m.name}</td>
                                            <td className="py-3 px-4 text-brand-inkSoft whitespace-nowrap">{m.flat_label || "—"}</td>
                                            <td className="py-3 px-4 whitespace-nowrap">
                                                <Chip variant={m.relationship_type === "owner" ? "warn" : "default"}>{m.relationship_label || "—"}</Chip>
                                            </td>
                                            <td className="py-3 px-4 text-brand-inkSoft whitespace-nowrap">
                                                {!m.has_mobile ? (
                                                    "—"
                                                ) : fullMobile ? (
                                                    <a data-testid={`directory-mobile-revealed-${key}`} href={`tel:${fullMobile}`} className="font-medium text-brand-ink hover:text-brand-action">{fullMobile}</a>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <span data-testid={`directory-mobile-masked-${key}`} className="tracking-wide">{m.mobile_masked}</span>
                                                        <button
                                                            type="button"
                                                            data-testid={`directory-mobile-view-btn-${key}`}
                                                            onClick={() => revealMobile(m)}
                                                            disabled={revealing.has(key)}
                                                            className="text-xs font-medium text-brand-action underline underline-offset-2 hover:text-brand-actionHover disabled:opacity-50"
                                                        >
                                                            {revealing.has(key) ? "…" : "View"}
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Dialog open={!!editingMember} onOpenChange={(open) => !open && setEditingMember(null)}>
                <DialogContent data-testid="designation-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit designation — {editingMember?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="text-xs text-brand-inkSoft mb-1.5 block">Designation</Label>
                            <Select value={designationChoice} onValueChange={setDesignationChoice}>
                                <SelectTrigger data-testid="designation-select">
                                    <SelectValue placeholder="Select designation" />
                                </SelectTrigger>
                                <SelectContent>
                                    {PRESET_DESIGNATIONS.map((d) => (
                                        <SelectItem key={d} value={d}>{d}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {designationChoice === "Custom" && (
                            <div>
                                <Label className="text-xs text-brand-inkSoft mb-1.5 block">Custom designation</Label>
                                <Input
                                    data-testid="designation-custom-input"
                                    value={customDesignation}
                                    onChange={(e) => setCustomDesignation(e.target.value)}
                                    placeholder="e.g. Cultural Head"
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditingMember(null)} data-testid="designation-cancel-btn">Cancel</Button>
                        <Button
                            data-testid="designation-save-btn"
                            onClick={saveDesignation}
                            disabled={saving || !designationChoice || (designationChoice === "Custom" && !customDesignation.trim())}
                            className="bg-brand-action hover:bg-brand-actionHover text-white"
                        >
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
